import { FCX } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { useRespondentTypesGroups } from "@core/CommonDataContext";
import { ComponentProps, useMemo } from "react";
import { TableColumn, TableColumnsContext } from "@components/Table/TableColumns";
import { Table } from "@components/Table";
import {
    ConsumerGroupsTableItem,
    useConsumerGroupsTableConfig,
    useConsumerGroupsTableMetrics, useGetSelectedChartMetricValue
} from "./ConsumerGroupsTableConfig";
import { useConsumerGroupsContext, View } from "@pages/Profile/components/ConsumerGroups/ConsumerGroups";
import { getCopyTableData } from "@components/Table/TableUtils";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import HorizontalBarChart from "@components/HorizontalBarChart";
import Chart from "@pages/Profile/components/ConsumerGroups/Chart";


const RespondentTypesGroupTable: FCX<{
    index: number;
    config: TableColumn[];
    isAccent?: boolean;
    itemsCount: number;
    title?: string;
}> = ({
    index,
    config,
    title,
    itemsCount,
    isAccent,
    testId,
}) => {
    const tableProviderValue = useMemo(
        () => ({
            data: config.map(i => ({
                ...i,
                accentLevel: isAccent ? 0 : 1,
            })),
        }),
        [config, isAccent]
    );

    return (
        <TableColumnsContext.Provider
            value={tableProviderValue}
        >
            <div
                data-testid={testId}
                className={cn(
                    "ProfileConsumerGroups__composite-table-part",
                    isAccent && 'is-accent',
                )}
            >
                {!!title && (
                    <div
                        className="ProfileConsumerGroups__composite-table-section-header TableCell accent-1"
                        data-testid={`textProfileCGRespondentTypesTitle${index}`}
                    >
                        {title.toUpperCase()}
                    </div>
                )}
                <Table
                    noHeader
                    rowsCount={itemsCount}
                />
            </div>
        </TableColumnsContext.Provider>
    );
};

const RespondentTypes: FCX = ({
    className,
    ...props
}) => {
    const {
        data,
        selectedView,
    } = useConsumerGroupsContext();
    const getTableConfig = useConsumerGroupsTableConfig();
    const getMetricsData = useConsumerGroupsTableMetrics();
    const respondentTypesGroups = useRespondentTypesGroups();

    const dataByGroups = useMemo(
        () => {
            const demographics = data.demographics ?? [];
            return respondentTypesGroups.map((group) => ({
                ...group,
                items: group.items.map(item => {
                    const itemData = demographics.find(i => i.id === item.id);
                    return {
                        ...item,
                        data: itemData,
                    };
                }),
            }));
        },
        [data.demographics, respondentTypesGroups]
    );

    const tableColumnsByGroups = useMemo(
        (): ConsumerGroupsTableItem[][] => dataByGroups.map((group) => {
            return group.items.map((item) => ({
                id: item.id,
                name: item.name ?? '',
                baseSize: item.data?.base ?? 0,
                metrics: getMetricsData(item.data?.metrics ?? []),
            }));
        }),
        [dataByGroups, getMetricsData]
    );

    const tableConfigs = useMemo(
        (): TableColumn[][] => tableColumnsByGroups.map((group) => {
            return getTableConfig(group);
        }),
        [tableColumnsByGroups, getTableConfig]
    );

    const getSelectedChartMetricValue = useGetSelectedChartMetricValue();

    const chartsData = useMemo(
        (): ComponentProps<typeof HorizontalBarChart>['data'] => {
            const mappedGroups = dataByGroups.map((group, groupIndex) => {
                const columnsData = tableColumnsByGroups[groupIndex];

                return {
                    id: group.id,
                    items: group.items.map((item, itemIndex) => {
                        const itemData = columnsData[itemIndex];
                        const { value, numValue } = getSelectedChartMetricValue(itemData);

                        return {
                            id: item.id,
                            label: item.name,
                            value,
                            numValue,
                        };
                    }),
                };
            });

            return mappedGroups.flatMap((group, index) =>
                index === 0
                ? [...group.items]
                : [
                    {
                        id: `separator-${group.id}`,
                        label: '',
                        value: '',
                        numValue: 0,
                        isHidden: true,
                    },
                    ...group.items,
                ]
            );
        },
        [dataByGroups, getSelectedChartMetricValue, tableColumnsByGroups]
    );

    const tableHeaderContextValue = useMemo(
        () => ({
            data: tableConfigs[0],
        }),
        [tableConfigs]
    );

    const copyTableContent = useMemo(
        (): string[][] => {
            return [
                getCopyTableData(tableConfigs[0], 0)[0],
                ...dataByGroups.flatMap((group, groupIndex) => {
                    let result: string[][] = [];

                    if (group.name) {
                        result.push([group.name]);
                    }

                    result = [
                        ...result,
                        ...getCopyTableData(tableConfigs[groupIndex], group.items.length).slice(1),
                    ];

                    return result;
                }),
            ];
        },
        [dataByGroups, tableConfigs]
    );

    return (
        <>
            <AsideButtons>
                <CopyTableButton
                    data={copyTableContent}
                    testId="buttonProfileCGCopyTable"
                />
            </AsideButtons>
            {selectedView === View.Table && (
                <div className={cn("ProfileConsumerGroups__composite-table", className)} {...props}>
                    <TableColumnsContext.Provider value={tableHeaderContextValue}>
                        <Table
                            rowsCount={0}
                            className="ProfileConsumerGroups__composite-table-header"
                        />
                    </TableColumnsContext.Provider>
                    {dataByGroups.map((group, groupIndex) => (
                        <RespondentTypesGroupTable
                            key={group.id}
                            index={groupIndex}
                            config={tableConfigs[groupIndex]}
                            itemsCount={group.items.length}
                            title={group.name}
                            isAccent={groupIndex === 0}
                            testId={`tableProfileCGRespondentTypes${groupIndex}`}
                        />
                    ))}
                </div>
            )}
            {selectedView === View.Chart && (
                <Chart data={chartsData}/>
            )}
        </>
    );
};

export default RespondentTypes;